import { useEffect, useState, type RefObject } from "react";

/**
 * Заголовок, выше которого читатель уже прокрутил страницу. Считается по
 * положению самих заголовков, а не по порядку сообщений наблюдателя: при первом
 * запуске он присылает сразу все заголовки, и разбирать их по одному значило бы
 * остановиться на последнем.
 */
// Верхний край полосы наблюдения и линия чтения: линия чуть ниже, чтобы событие,
// пришедшее с запозданием на кадр, не отставало от прокрутки.
const BAND_TOP = 140;
const READING_LINE = BAND_TOP + 12;

function headingAtReadingLine(ids: readonly string[]): string | null {
  let current: string | null = ids[0] ?? null;
  for (const id of ids) {
    const element = document.getElementById(id);
    if (element === null) continue;
    if (element.getBoundingClientRect().top > READING_LINE) break;
    current = id;
  }
  return current;
}

/**
 * Какой раздел читают сейчас. Наблюдатель лишь сообщает, что какой-то заголовок
 * пересёк верхнюю полосу окна (выше неё лежит шапка ERP), а сам ответ — последний
 * заголовок над линией чтения — считается по его положению.
 */
export function useActiveHeading(
  ids: readonly string[],
  enabled: boolean,
): string | null {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    if (
      !enabled ||
      ids.length === 0 ||
      typeof IntersectionObserver === "undefined"
    ) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        // Без геометрии (тестовая заглушка) верим самому событию.
        if (entries.some((entry) => entry.boundingClientRect !== undefined)) {
          setActive(headingAtReadingLine(ids));
          return;
        }
        for (const entry of entries) {
          if (entry.isIntersecting) setActive(entry.target.id);
        }
      },
      { rootMargin: `-${BAND_TOP}px 0px -60% 0px` },
    );
    for (const id of ids) {
      const element = document.getElementById(id);
      if (element !== null) observer.observe(element);
    }

    // Наблюдатель видит только пересечение полосы, а прокрутка к разделу по
    // оглавлению может закончиться, не пересекая её. Поэтому, когда прокрутка
    // стихла, положение проверяется ещё раз.
    let settle: number | undefined;
    const onScroll = () => {
      window.clearTimeout(settle);
      settle = window.setTimeout(
        () => setActive(headingAtReadingLine(ids)),
        150,
      );
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.clearTimeout(settle);
      window.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [ids, enabled]);

  return enabled ? active : null;
}

/** Показать кнопку «Наверх»: верх страницы ушёл за край окна. */
export function useScrolledAway(
  sentinel: RefObject<HTMLElement | null>,
): boolean {
  const [away, setAway] = useState(false);

  useEffect(() => {
    const element = sentinel.current;
    if (element === null || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry !== undefined) setAway(!entry.isIntersecting);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [sentinel]);

  return away;
}

/** Раз в минуту, чтобы «проверено 3 минуты назад» не застывало на экране. */
export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);

  return now;
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
